import { offlineStorage } from './offlineStorage';

export type OperationType = 'CREATE' | 'UPDATE' | 'DELETE';
export type Priority = 'urgent' | 'normal' | 'low';
export type SyncStatus = 'pending' | 'syncing' | 'success' | 'failed';

export interface SyncOperation {
  id: string;
  type: OperationType;
  resource: 'activity' | 'photo' | 'daily_record' | 'voice' | 'score';
  data: any;
  priority: Priority;
  status: SyncStatus;
  retryCount: number;
  maxRetries: number;
  lastAttempt?: Date;
  error?: string;
  createdAt: Date;
}

export interface ConflictData {
  id: string;
  resource: string;
  localVersion: any;
  serverVersion: any;
  timestamp: Date;
}

class SyncQueueManager {
  private queue: SyncOperation[] = [];
  private isSyncing = false;
  private readonly BASE_RETRY_DELAY = 1000; // 1 second
  private readonly MAX_RETRY_DELAY = 60000; // 60 seconds
  private onQueueChangeCallback?: (queue: SyncOperation[]) => void;
  private conflicts: ConflictData[] = [];

  constructor() {
    this.loadQueue();
    this.setupNetworkListeners();
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('Network restored, starting sync...');
      this.processBatch();
    });

    window.addEventListener('offline', () => {
      console.log('Network lost, pausing sync');
      this.isSyncing = false;
    });
  }

  private loadQueue() {
    try {
      const stored = localStorage.getItem('sync_queue');
      if (stored) {
        this.queue = JSON.parse(stored).map((op: any) => ({
          ...op,
          createdAt: new Date(op.createdAt),
          lastAttempt: op.lastAttempt ? new Date(op.lastAttempt) : undefined,
        }));
      }
    } catch (error) {
      console.error('Error loading sync queue:', error);
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem('sync_queue', JSON.stringify(this.queue));
      this.onQueueChangeCallback?.(this.queue);
    } catch (error) {
      console.error('Error saving sync queue:', error);
    }
  }

  onQueueChange(callback: (queue: SyncOperation[]) => void) {
    this.onQueueChangeCallback = callback;
  }

  addOperation(
    type: OperationType,
    resource: SyncOperation['resource'],
    data: any,
    priority: Priority = 'normal'
  ): string {
    const operation: SyncOperation = {
      id: `${resource}_${Date.now()}_${Math.random()}`,
      type,
      resource,
      data,
      priority,
      status: 'pending',
      retryCount: 0,
      maxRetries: 3,
      createdAt: new Date(),
    };

    this.queue.push(operation);
    this.sortQueueByPriority();
    this.saveQueue();

    // Auto-start sync if online
    if (navigator.onLine && !this.isSyncing) {
      this.processBatch();
    }

    return operation.id;
  }

  private sortQueueByPriority() {
    const priorityOrder = { urgent: 0, normal: 1, low: 2 };
    const resourceOrder: Record<string, number> = { photo: 0, daily_record: 1, voice: 2, activity: 3, score: 4 };
    this.queue.sort((a, b) => {
      if (a.status === 'failed' && b.status !== 'failed') return 1;
      if (a.status !== 'failed' && b.status === 'failed') return -1;
      const pDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (pDiff !== 0) return pDiff;
      return (resourceOrder[a.resource] ?? 99) - (resourceOrder[b.resource] ?? 99);
    });
  }

  async processBatch(maxItems: number = 5): Promise<void> {
    if (this.isSyncing || !navigator.onLine) {
      console.log('Sync already in progress or offline');
      return;
    }

    this.isSyncing = true;

    try {
      const pendingOps = this.queue
        .filter(op => op.status === 'pending' || op.status === 'failed')
        .slice(0, maxItems);

      if (pendingOps.length === 0) {
        console.log('No pending operations to sync');
        this.isSyncing = false;
        return;
      }

      console.log(`Processing ${pendingOps.length} operations...`);

      for (const operation of pendingOps) {
        await this.processOperation(operation);
      }

      // Remove successful operations
      this.queue = this.queue.filter(op => op.status !== 'success');
      this.saveQueue();
    } catch (error) {
      console.error('Error processing batch:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  private async processOperation(operation: SyncOperation): Promise<void> {
    operation.status = 'syncing';
    operation.lastAttempt = new Date();
    this.saveQueue();

    try {
      // Simulate API call with delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In production, make actual API calls here based on operation type
      switch (operation.resource) {
        case 'activity':
          await this.syncActivity(operation);
          break;
        case 'photo':
          await this.syncPhoto(operation);
          break;
        case 'daily_record':
          await this.syncDailyRecord(operation);
          break;
        case 'voice':
          await this.syncVoice(operation);
          break;
        case 'score':
          await this.syncScore(operation);
          break;
      }

      operation.status = 'success';
      console.log(`Successfully synced operation: ${operation.id}`);
    } catch (error: unknown) {
      console.error(`Error syncing operation ${operation.id}:`, error);

      operation.retryCount++;
      operation.error = error instanceof Error ? error.message : String(error);

      if (operation.retryCount >= operation.maxRetries) {
        operation.status = 'failed';
        console.error(`Operation ${operation.id} failed after ${operation.maxRetries} retries`);
      } else {
        operation.status = 'pending';
        // Schedule retry with exponential backoff
        const delay = Math.min(
          this.BASE_RETRY_DELAY * Math.pow(2, operation.retryCount),
          this.MAX_RETRY_DELAY
        );
        setTimeout(() => this.processBatch(), delay);
      }
    }

    this.saveQueue();
  }

  private async syncActivity(operation: SyncOperation): Promise<void> {
    // Simulate API call
    console.log(`Syncing activity: ${operation.type}`, operation.data);
    
    // Check for conflicts (simplified)
    const hasConflict = Math.random() < 0.1; // 10% chance of conflict
    if (hasConflict && operation.type === 'UPDATE') {
      this.conflicts.push({
        id: operation.id,
        resource: 'activity',
        localVersion: operation.data,
        serverVersion: { ...operation.data, notes: 'Server version' },
        timestamp: new Date(),
      });
      throw new Error('Conflict detected');
    }

    // Mark as synced in offline storage
    if (operation.data.id) {
      await offlineStorage.markActivitySynced(operation.data.id);
    }
  }

  private async syncPhoto(operation: SyncOperation): Promise<void> {
    console.log(`Syncing photo: ${operation.type}`, operation.data);
    // Actual photo upload would happen here
  }

  private async syncVoice(operation: SyncOperation): Promise<void> {
    console.log(`Syncing voice recording: ${operation.type}`, operation.data);
  }

  private async syncDailyRecord(operation: SyncOperation): Promise<void> {
    console.log(`Syncing daily record: ${operation.type}`, operation.data);
    // Import dynamically to avoid circular deps
    const { edgeFunctions } = await import('@/services/edgeFunctions');
    const { error } = await edgeFunctions.processDailyRecord(operation.data);
    if (error) throw error;

    // Mark as synced in offline storage
    if (operation.data._offlineId) {
      await offlineStorage.markDailyRecordSynced(operation.data._offlineId);
    }
  }

  private async syncScore(operation: SyncOperation): Promise<void> {
    console.log(`Syncing score: ${operation.type}`, operation.data);
    // Actual score sync would happen here
  }

  getQueue(): SyncOperation[] {
    return [...this.queue];
  }

  getPendingCount(): number {
    return this.queue.filter(op => op.status === 'pending').length;
  }

  getFailedCount(): number {
    return this.queue.filter(op => op.status === 'failed').length;
  }

  getConflicts(): ConflictData[] {
    return [...this.conflicts];
  }

  resolveConflict(conflictId: string, resolution: 'local' | 'server' | 'merge', mergedData?: any) {
    const conflictIndex = this.conflicts.findIndex(c => c.id === conflictId);
    if (conflictIndex === -1) return;

    const conflict = this.conflicts[conflictIndex];
    const operation = this.queue.find(op => op.id === conflictId);

    if (operation) {
      if (resolution === 'local') {
        operation.data = conflict.localVersion;
      } else if (resolution === 'server') {
        operation.data = conflict.serverVersion;
      } else if (resolution === 'merge' && mergedData) {
        operation.data = mergedData;
      }

      operation.status = 'pending';
      operation.retryCount = 0;
      this.saveQueue();
    }

    this.conflicts.splice(conflictIndex, 1);
  }

  async retryFailed(): Promise<void> {
    const failedOps = this.queue.filter(op => op.status === 'failed');
    failedOps.forEach(op => {
      op.status = 'pending';
      op.retryCount = 0;
      op.error = undefined;
    });
    this.saveQueue();
    
    if (navigator.onLine) {
      await this.processBatch();
    }
  }

  clearQueue(): void {
    this.queue = [];
    this.saveQueue();
  }

  removeOperation(id: string): void {
    this.queue = this.queue.filter(op => op.id !== id);
    this.saveQueue();
  }
}

export const syncQueue = new SyncQueueManager();
